// Route constants for this tool only. Deliberately not added to
// `src/navigation/AppParamList.ts` — internalTools stay isolated from the
// product's navigation tree (see this folder's README).

export const DASHBOARD_BASE = "/admin/master-dashboard";

export const dashboardPath = {
  home: () => `${DASHBOARD_BASE}/home`,
  customers: () => `${DASHBOARD_BASE}/customers`,
  customer: (uid: string) => `${DASHBOARD_BASE}/customers/${uid}`,
  events: () => `${DASHBOARD_BASE}/events`,
  referrals: () => `${DASHBOARD_BASE}/referrals`,
  instagram: () => `${DASHBOARD_BASE}/instagram`,
  instagramDms: () => `${DASHBOARD_BASE}/instagram/dms`,
  payments: () => `${DASHBOARD_BASE}/payments`,
};

export interface NavItem {
  label: string;
  icon: string;
  path?: string;
  /** Reserved slots render in place but greyed out, with a "Soon" tag. */
  enabled: boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * **"Legacy" is gone as a section label, deliberately.**
 *
 * It named these screens' *provenance* — carried over from the old
 * `/adminDashboard` — which was a useful thing to say while that dashboard
 * still existed and a reader might wonder which of the two to open. With
 * Payments ported, nothing is left there, the old dashboard is deleted, and
 * "Legacy" now describes a distinction the product no longer has. Worse, it
 * reads as "deprecated, don't trust this", which is the opposite of true: these
 * are current screens on current data.
 *
 * So the sections are named for what the screens are *about*:
 *
 * - **Engagement** — Events, Referrals, Instagram. What creators and their
 *   audiences do. All three are per-record browsable lists of platform
 *   activity.
 * - **Revenue** — Payments. The only screen about individual money movements,
 *   and the only one opened for a support reason ("did this person's payment go
 *   through?") rather than a reporting one. Grouping a Stripe ledger under the
 *   same heading as Instagram automations would be a grab-bag; giving it its
 *   own section also leaves the obvious slot for payouts or refunds later.
 *   Single-item sections are already this file's own precedent (Overview,
 *   Customers).
 *
 * Failed payments live under "Revenue" too. They are attempted revenue, which
 * is how Stripe's own dashboard groups them, and splitting them into a separate
 * section would bury the tab that makes the screen useful.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: "Overview",
    items: [
      {
        label: "Home",
        icon: "mdi:home-variant",
        path: dashboardPath.home(),
        enabled: true,
      },
    ],
  },
  {
    label: "Customers",
    items: [
      {
        label: "Customers",
        icon: "ion:people",
        path: dashboardPath.customers(),
        enabled: true,
      },
    ],
  },
  {
    label: "Revenue",
    items: [
      {
        label: "Payments",
        icon: "ion:card",
        path: dashboardPath.payments(),
        enabled: true,
      },
    ],
  },
  {
    label: "Engagement",
    items: [
      {
        label: "Events",
        icon: "ion:calendar",
        path: dashboardPath.events(),
        enabled: true,
      },
      {
        label: "Referrals",
        icon: "ion:gift",
        path: dashboardPath.referrals(),
        enabled: true,
      },
      {
        label: "Instagram",
        icon: "ion:flash",
        path: dashboardPath.instagram(),
        enabled: true,
      },
    ],
  },
];
